import _ from 'lodash';
import pluralize from 'pluralize';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation, useParams, useRouteMatch } from 'react-router-dom';
import styled from 'styled-components';
import {
  ActionsBanner,
  Avatar,
  ClientFilter,
  ColumnsButton,
  ColumnsDrawer,
  CompanyFilter,
  Confirmation,
  CreateButton,
  DateTime,
  ExportDialog,
  ExportDropdown,
  FiltersBar,
  Icon,
  ListView,
  ListViewActions,
  ListViewMenu,
  ListViewSelection,
  MemberContactPopover,
  OpportunityFilter,
  Page,
  SearchInput,
  Tag,
  Tags,
  Tooltip,
  YesNoFilter,
} from '~/components';
import { BulkActionMenu } from '~/components/bulk-action';
import {
  ContactFiltersBar,
  ContactFiltersGroup,
  FilterButton,
  FiltersDrawer,
  FiltersGroup,
} from '~/components/filters';
import { useApi, useConfirmation, useIntegrations, useToast, useWorkspace } from '~/contexts';
import {
  useAuth,
  useContactFilters,
  useDocumentTitle,
  useFeatures,
  useIsMounted,
  useRowSelection,
  useSearchParams,
  useSearchParamsConfig,
  useViewColumns,
} from '~/hooks';
import { PageLoader } from '~/routes/public/pages';
import { colors, weights } from '~/styles';
import { QuerySort, mimeTypes } from '~/utils';
import HubspotIndicator from '../components/HubspotIndicator.jsx';
import SalesforceIndicator from '../components/SalesforceIndicator';
import ContactForm from './ContactForm.jsx';
import { BulkEditContact } from './bulk';

const ContactInfo = styled.div`
  line-height: 1;
  align-items: center;
`;

const ContactName = styled.div`
  display: flex;
  align-items: center;
`;

const ContactTitle = styled.small`
  font-size: 0.75rem;
  color: ${colors.grey40};
  display: flex;
  padding-top: 0.25rem;
`;

const AssociatedTo = styled.p`
  color: ${colors.grey40};
  font-size: 0.75rem;
  font-weight: ${weights.black};
  letter-spacing: 0.0625rem;
  text-transform: uppercase;
  margin-bottom: 0.5rem;
  margin-left: 0.25rem;
`;

const Title = styled.p`
  color: ${colors.grey40};
  font-size: 0.75rem;
  font-weight: ${weights.black};
  letter-spacing: 0.0625rem;
  text-transform: uppercase;
  margin-bottom: 0.5rem;
  margin-left: 0.25rem;
`;

const Owner = styled.div`
  display: flex;
  align-items: center;
`;

const OwnerInfo = styled.span`
  margin-left: 0.5rem;
`;

function FirstAssignment({ assignments }) {
  if (assignments.length === 0) return null;

  const firstAssignment = assignments[0];
  let color;

  switch (firstAssignment.type) {
    case 'client':
      color = colors.primary10;
      break;
    case 'company':
      color = colors.warning10;
      break;
    case 'opportunity':
      color = colors.grey5;
      break;
    default:
      color = colors.black;
  }

  return (
    <Tag style={{ backgroundColor: color, color: colors.black }}>
      <small>{firstAssignment.name}</small>
      {firstAssignment.isPrimary && <Icon color={colors.grey40} spaceLeft icon="star" size="xs" />}
    </Tag>
  );
}

function Assignments({ assignments }) {
  if (assignments.length <= 1) return null;

  const getColor = (type) => {
    switch (type) {
      case 'client':
        return colors.primary10;
      case 'company':
        return colors.warning10;
      case 'opportunity':
        return colors.grey5;
      default:
        return colors.black;
    }
  };

  const assignmentsByType = assignments.reduce((acc, assignment) => {
    acc[assignment.type] = acc[assignment.type] || [];
    acc[assignment.type].push(assignment);
    acc[assignment.type] = _.orderBy(acc[assignment.type], ['name'], ['asc']);

    return acc;
  }, {});

  return (
    <Tooltip
      message={
        <div style={{ fontSize: '1rem', marginBottom: '-0.75rem' }}>
          {_.map(assignmentsByType, (assignments, type) => (
            <div key={type} style={{ marginBottom: '1rem' }}>
              <AssociatedTo>{pluralize(type)}</AssociatedTo>
              {assignments.map((assignment, index) => (
                <Tag style={{ backgroundColor: getColor(assignment.type) }} key={index}>
                  <small>{assignment.name}</small>
                  {assignment.isPrimary && <Icon color={colors.grey40} spaceLeft icon="star" size="xs" />}
                </Tag>
              ))}
            </div>
          ))}
        </div>
      }>
      <Tag style={{ backgroundColor: colors.grey5, color: colors.grey40 }}>
        <small>+{assignments.length - 1}</small>
      </Tag>
    </Tooltip>
  );
}

function FirstContactTag({ tags }) {
  const firstTag = tags && tags[0];
  if (!firstTag) return null;

  return (
    <Tag style={{ backgroundColor: colors.grey5 }}>
      <small>{firstTag.name}</small>
    </Tag>
  );
}

function ContactTags({ tags = [] }) {
  const count = tags.length - 1;
  if (count <= 0) return null;

  return (
    <Tooltip
      message={
        <div style={{ fontSize: '1rem' }}>
          <Title>Tags</Title>
          {tags.map((tag) => (
            <Tag style={{ backgroundColor: colors.grey5 }} key={tag.id}>
              <small>{tag.name}</small>
            </Tag>
          ))}
        </div>
      }>
      <Tag style={{ backgroundColor: colors.grey5, color: colors.grey40 }}>
        <small>+{count}</small>
      </Tag>
    </Tooltip>
  );
}

export default function ContactsListPage({ sessionKey }) {
  const documentTitle = useDocumentTitle('Contacts');

  const { workspace } = useWorkspace();
  const integrations = useIntegrations();
  const api = useApi();
  const auth = useAuth();
  const features = useFeatures();
  const history = useHistory();
  const isMounted = useIsMounted();
  const location = useLocation();
  const { contactId } = useParams();
  const toast = useToast();
  const { url } = useRouteMatch();
  const confirmation = useConfirmation();

  const searchParamsConfig = useSearchParamsConfig();

  const contactFilters = useContactFilters();

  const [query, setQuery] = useState({
    isReady: false,
    searchParamsStatus: 'pending',
    data: null,
    params: {
      q: '',
      contactHubspotSynced: null,
      contactSalesforceSynced: null,
      clients: [],
      companies: [],
      opportunities: [],
      page: 0,
      size: 25,
      sort: new QuerySort('name', 'asc'),
      ...contactFilters.filters,
    },
    action: 'load',
  });

  const setParams = (params) => {
    setQuery((state) => ({
      ...state,
      action: 'filter',
      params: { ...state.params, ...params, page: 0 },
      searchParamsStatus: 'ready',
    }));
  };

  const loadMore = useCallback(() => {
    setQuery((state) => {
      if (
        state.searchParamsStatus !== 'ready' ||
        state.action !== null ||
        !state.data ||
        state.data.total <= state.data.results.length
      ) {
        return state;
      }

      return {
        ...state,
        params: { ...state.params, page: state.params.page + 1 },
        action: 'load-more',
      };
    });
  }, []);

  const removeItem = (id) => {
    setQuery((state) => ({
      ...state,
      data: {
        ...state.data,
        results: state.data?.results.filter((i) => i.id !== id),
        total: state.data.total - 1,
      },
    }));
  };

  const updateItem = (item) => {
    setQuery((state) => ({
      ...state,
      data: {
        ...state.data,
        results: state.data?.results.map((i) => (i.id === item.id ? { ...i, ...item } : i)),
      },
    }));
  };

  const { action, data, isReady, params, searchParamsStatus } = query;

  const selection = useRowSelection({ rows: data?.results });

  const searchParams = useSearchParams({
    config: useMemo(
      () => ({
        q: { default: '' },
        clients: searchParamsConfig.clients,
        companies: searchParamsConfig.companies,
        opportunities: searchParamsConfig.opportunities,
        contactHubspotSynced: { default: null },
        contactSalesforceSynced: { default: null },
        contactTags: { default: [], ...searchParamsConfig.contactTags },
        sort: { default: new QuerySort('name', 'asc'), ...searchParamsConfig.sort },
        ...contactFilters.searchParamsConfig,
      }),
      [searchParamsConfig, contactFilters],
    ),
    sessionKey,
    onChange: useCallback((params) => setParams((state) => ({ ...state, ...params })), []),
  });

  // Map the values to perform the API query
  const urlSearchParams = useMemo(
    () => ({
      q: params.q,
      clientId: params.clients?.map((v) => v.id),
      companyId: params.companies?.map((v) => v.id),
      opportunityId: params.opportunities?.map((v) => v.id),
      contactHubspotSynced:
        integrations?.hubspot && params.contactHubspotSynced ? params.contactHubspotSynced : undefined,
      contactSalesforceSynced:
        integrations?.salesforce && params.contactSalesforceSynced ? params.contactSalesforceSynced : undefined,
      contactTagId: params.contactTags?.length ? params.contactTags.map((t) => t.id) : undefined,
      page: params.page,
      size: params.size,
      sort: params.sort,
      ...contactFilters.mapUrlSearchParams(params),
    }),
    [params, contactFilters, integrations?.hubspot, integrations?.salesforce],
  );

  useEffect(() => {
    if (searchParamsStatus !== 'pending') return;
    searchParams.get().then((params) => {
      if (params) setParams(params);
    });
  }, [searchParams, searchParamsStatus]);

  const fetchData = useCallback(async () => {
    try {
      const { data } = await api.www.workspaces(workspace.id).contacts().get(urlSearchParams);

      if (!isMounted.current) return;

      setQuery((state) => ({
        ...state,
        action: null,
        searchParamsStatus: 'ready',
        data: {
          ...data,
          results: state.action === 'load-more' ? [...state.data.results, ...data.results] : data.results,
          total: data.total,
        },
      }));
    } catch (error) {
      setQuery((state) => ({ ...state, data: { total: 0, results: [] } }));
      toast.error(error.message);
    }
  }, [workspace.id, api, isMounted, urlSearchParams, toast]);

  useEffect(() => {
    if (searchParamsStatus !== 'ready') return;
    fetchData();
  }, [fetchData, searchParamsStatus]);

  const [filtersVisible, setFiltersVisible] = useState(false);
  const showFilters = () => setFiltersVisible(true);
  const hideFilters = () => setFiltersVisible(false);

  const handleFilter = (values) => {
    selection.clear();
    setParams({ ...values });
    searchParams.set({ ..._.omit(values, 'sort') });
  };

  const handleSort = ({ column, sort }) => {
    const direction = column === sort.column && sort.direction === 'asc' ? 'desc' : 'asc';
    const querySort = new QuerySort(column, direction);
    selection.clear();
    setParams({ sort: querySort });
    searchParams.set({ sort: querySort });
  };

  const handleBulkEdit = async () => {
    await confirmation.prompt((resolve) => (
      <BulkEditContact
        ids={selection.ids}
        onComplete={async () => {
          selection.clear();
          // A practice or business unit change can move a contact out of this member's own scope, so
          // refetch rather than patching rows in place. Accumulated pages are preserved.
          if (params.page !== 0) await fetchData();
          else setParams({ page: 0 });
        }}
        onClose={resolve}
      />
    ));
  };

  async function handleSaved(contact) {
    const { data } = await api.www.workspaces(workspace.id).contacts().get({ contactId: contact.id });
    updateItem(data[0]);
  }

  async function handleDeleted(contact) {
    removeItem(contact.id);
  }

  const handleDeleteConfirmation = async (contact, { redirect = false } = {}) => {
    const confirm = await confirmation.prompt((resolve) => (
      <Confirmation resolve={resolve}>Are you sure you want to delete this contact?</Confirmation>
    ));

    if (!confirm) return;
    await api.www.workspaces(workspace.id).contacts(contact.id).delete();

    handleDeleted(contact);
    if (redirect) handleClose();
  };

  const handleEdit = ({ id }) => {
    history.push({
      pathname: `/app/${workspace.key}/pipeline/contacts/edit/${id}`,
      search: location.search,
      state: { scrollToTop: false },
    });
  };

  const handleOverview = ({ id }) => {
    history.push(`/app/${workspace.key}/pipeline/contacts/${id}/overview`);
  };

  function handleClose() {
    history.push({
      pathname: `/app/${workspace.key}/pipeline/contacts`,
      search: location.search,
      state: { scrollToTop: false },
    });
    documentTitle.set('Contacts');
  }

  const handleExport = async (filename, mimeType) => {
    await confirmation.prompt((resolve) => (
      <ExportDialog
        filename={filename}
        onLoad={api.www
          .workspaces(workspace.id)
          .contacts()
          .export(
            { ...urlSearchParams, size: null, page: null },
            {
              headers: { accept: mimeType },
              responseType: 'blob',
            },
          )}
        onClose={resolve}
      />
    ));
  };

  const [dialog, setDialog] = useState(null);

  const columns = useViewColumns({
    storageKey: 'contacts_list_columns',
    // Default off: useViewColumns reads any saved key list, so a column defaulting to on would
    // stay invisible for anyone who already applied column preferences.
    columns: _.compact([
      { key: 'contact', name: 'Name', disabled: true },
      { key: 'assignedTo', name: 'Assigned To', default: true },
      { key: 'email', name: 'Email', default: true },
      { key: 'owner', name: 'Owner', default: false },
      { key: 'tags', name: 'Tags', default: true },
      features.practices && { key: 'practice', name: 'Practice', default: false },
      features.businessUnits && { key: 'businessUnit', name: 'Business Unit', default: false },
      { key: 'createdAt', name: 'Created', default: true },
    ]),
  });

  const handleShowColumnsDrawer = () => {
    setDialog(
      <ColumnsDrawer
        value={columns.selected}
        columns={columns.all}
        onClose={() => setDialog(null)}
        onApply={(value) => {
          columns.save(value);
          setDialog(null);
        }}
      />,
    );
  };

  if (!isReady && !data) return <PageLoader />;

  const salesforceEnabled = integrations?.salesforce;
  const hubspotEnabled = integrations?.hubspot;
  // Bulk edit is the only bulk action, so edit alone gates the selection column.
  const hasSelectionColumn = auth.contacts.edit;

  return (
    <>
      <Page>
        <Page.Header>
          <Page.Info>
            <Page.Eyebrow>Pipeline</Page.Eyebrow>
            <Page.Title>Contacts</Page.Title>
          </Page.Info>

          <Page.Actions>
            <ExportDropdown>
              {({ setIsOpen }) => (
                <>
                  <ExportDropdown.Item
                    onClick={async () => {
                      await handleExport(`contacts.csv`, mimeTypes.csv);
                      setIsOpen(false);
                    }}>
                    Export to CSV
                  </ExportDropdown.Item>

                  <ExportDropdown.Item
                    onClick={async () => {
                      await handleExport(`contacts.xlsx`, mimeTypes.xlsx);
                      setIsOpen(false);
                    }}>
                    Export to Excel
                  </ExportDropdown.Item>
                </>
              )}
            </ExportDropdown>

            <ColumnsButton isOutline onClick={handleShowColumnsDrawer} />

            <FilterButton isOutline onClick={showFilters} />

            <CreateButton
              tooltip={
                auth.contacts.create ? 'New Contact' : 'Your security role prohibits you from creating contacts.'
              }
              onClick={() => history.push(`${url}/new`)}
              disabled={!auth.contacts.create}
            />
          </Page.Actions>
        </Page.Header>

        <>
          <Page.Filters>
            <FiltersBar>
              <SearchInput
                value={params.q}
                placeholder="Search"
                onChange={({ target: { value } }) => handleFilter({ q: value })}
              />

              <ClientFilter
                name="clients"
                placeholder="Client"
                value={params.clients}
                onChange={({ target: { value } }) => handleFilter({ clients: value })}
              />

              <CompanyFilter
                name="companies"
                placeholder="Company"
                value={params.companies}
                onChange={({ target: { value } }) => handleFilter({ companies: value })}
              />

              <OpportunityFilter
                name="opportunities"
                placeholder="Opportunity"
                value={params.opportunities}
                onChange={({ target: { value } }) => handleFilter({ opportunities: value })}
              />

              <ContactFiltersBar filters={params} sticky={['contactOwners']} onChange={handleFilter} />

              {hubspotEnabled && (
                <YesNoFilter
                  icon="filter"
                  clearable
                  placeholder="HubSpot Synced"
                  value={params.contactHubspotSynced}
                  onChange={({ target: { value } }) => handleFilter({ contactHubspotSynced: value })}
                />
              )}

              {salesforceEnabled && (
                <YesNoFilter
                  icon="filter"
                  clearable
                  placeholder="Salesforce Synced"
                  value={params.contactSalesforceSynced}
                  onChange={({ target: { value } }) => handleFilter({ contactSalesforceSynced: value })}
                />
              )}
            </FiltersBar>
          </Page.Filters>
          <Filters values={params} isOpen={filtersVisible} onApply={handleFilter} onClose={hideFilters} />
        </>

        <Page.ListView>
          <ListView>
            <ListView.Header>
              {hasSelectionColumn && (
                <ListViewSelection.Column
                  selection={selection}
                  disabled={data.results.length === 0 || action === 'filter'}
                />
              )}
              <ListView.Column sticky minWidth="16rem" name="name" onSort={handleSort} sort={params.sort}>
                Name
              </ListView.Column>
              <ListView.Column minWidth="16rem" isVisible={columns.visible.assignedTo}>
                Assigned To
              </ListView.Column>
              <ListView.Column
                minWidth="16rem"
                name="email"
                onSort={handleSort}
                sort={params.sort}
                isVisible={columns.visible.email}>
                Email
              </ListView.Column>
              <ListView.Column
                minWidth="14rem"
                name="owner.name"
                onSort={handleSort}
                sort={params.sort}
                isVisible={columns.visible.owner}>
                Owner
              </ListView.Column>
              <ListView.Column minWidth="10rem" isVisible={columns.visible.tags}>
                Tags
              </ListView.Column>
              <ListView.Column
                width="12rem"
                name="practice.name"
                onSort={handleSort}
                sort={params.sort}
                isVisible={features.practices && columns.visible.practice}>
                Practice
              </ListView.Column>
              <ListView.Column
                width="12rem"
                name="businessUnit.name"
                onSort={handleSort}
                sort={params.sort}
                isVisible={features.businessUnits && columns.visible.businessUnit}>
                Business Unit
              </ListView.Column>
              <ListView.Column
                width="8rem"
                name="createdAt"
                onSort={handleSort}
                sort={params.sort}
                align="right"
                isVisible={columns.visible.createdAt}>
                Created
              </ListView.Column>
              {salesforceEnabled && (
                <ListView.Column width="4rem" align="left">
                  <SalesforceIndicator variant="grey" />
                </ListView.Column>
              )}
              {hubspotEnabled && (
                <ListView.Column width="4rem" align="left">
                  <HubspotIndicator variant="grey" />
                </ListView.Column>
              )}
              <ListViewActions.Column />
            </ListView.Header>

            <ListView.Body fade={action === 'filter'}>
              {query.data.results.map((contact) => {
                const {
                  id,
                  email,
                  name,
                  title,
                  createdAt,
                  permissions,
                  assignments,
                  salesforceId,
                  hubspotId,
                  owner,
                  practice,
                  businessUnit,
                } = contact;

                async function handleRowClick(contact) {
                  handleOverview(contact);
                }

                return (
                  <ListView.Row key={id} onClick={() => handleRowClick(contact)}>
                    {hasSelectionColumn && <ListViewSelection.Cell id={id} selection={selection} />}
                    <ListView.Cell>
                      <ContactInfo>
                        <ContactName>{name}</ContactName>

                        {title && <ContactTitle>{title}</ContactTitle>}
                      </ContactInfo>
                    </ListView.Cell>
                    <ListView.Cell>
                      <Tags>
                        <FirstAssignment assignments={assignments} />
                        <Assignments assignments={assignments} />
                      </Tags>
                    </ListView.Cell>
                    <ListView.Cell>{email}</ListView.Cell>
                    <ListView.Cell>
                      {owner && (
                        <Owner>
                          <MemberContactPopover member={owner} placement="left">
                            <Avatar value={owner} isCircle hasBackground initialsFontSize=".9rem" />
                          </MemberContactPopover>
                          <OwnerInfo>{owner.name}</OwnerInfo>
                        </Owner>
                      )}
                    </ListView.Cell>
                    <ListView.Cell>
                      <Tags>
                        <FirstContactTag tags={contact.tags} />
                        <ContactTags tags={contact.tags} />
                      </Tags>
                    </ListView.Cell>
                    <ListView.Cell>{practice?.name}</ListView.Cell>
                    <ListView.Cell>{businessUnit?.name}</ListView.Cell>
                    <ListView.Cell>
                      <DateTime value={createdAt} />
                    </ListView.Cell>
                    {salesforceEnabled && <ListView.Cell>{salesforceId && <SalesforceIndicator />}</ListView.Cell>}
                    {hubspotEnabled && <ListView.Cell>{hubspotId && <HubspotIndicator />}</ListView.Cell>}

                    <ListViewActions>
                      {permissions.edit ? (
                        <ListViewActions.Edit onClick={() => handleEdit(contact)} />
                      ) : (
                        <ListViewActions.View onClick={() => handleOverview(contact)} />
                      )}

                      <hr />

                      <ListViewMenu>
                        {({ setIsOpen }) => {
                          const handleAction = (action) => setIsOpen(false) || action;

                          return (
                            <>
                              <ListViewMenu.Item onClick={() => handleAction(handleOverview(contact))}>
                                View
                              </ListViewMenu.Item>

                              <ListViewMenu.Item
                                disabled={!permissions.edit}
                                tooltip={
                                  !permissions.edit ? 'Insufficient permissions to edit this contact.' : undefined
                                }
                                onClick={() => handleAction(handleEdit(contact))}>
                                Edit
                              </ListViewMenu.Item>

                              <ListViewMenu.Item
                                disabled={!permissions.delete}
                                tooltip={
                                  !permissions.delete ? 'Insufficient permissions to delete this contact.' : undefined
                                }
                                onClick={() => handleAction(handleDeleteConfirmation(contact))}>
                                Delete
                              </ListViewMenu.Item>
                            </>
                          );
                        }}
                      </ListViewMenu>
                    </ListViewActions>
                  </ListView.Row>
                );
              })}

              {query.data.results.length === 0 && <ListView.Empty />}

              {query.data.total > query.data.results.length && (
                <ListView.Loader key={query.data.results.length} onIntersecting={loadMore} />
              )}
            </ListView.Body>

            <ListView.Status total={query.data.total} label="Contact" isLoading={!!action} />
          </ListView>
        </Page.ListView>

        {selection.any && (
          <ActionsBanner>
            <BulkActionMenu count={selection.count}>
              {({ setIsOpen }) => (
                <BulkActionMenu.Item
                  onClick={() => {
                    handleBulkEdit();
                    setIsOpen(false);
                  }}>
                  Edit
                </BulkActionMenu.Item>
              )}
            </BulkActionMenu>
          </ActionsBanner>
        )}
      </Page>
      {contactId && (
        <ContactForm
          onSaved={handleSaved}
          onClose={handleClose}
          onDeleted={(contact) => handleDeleteConfirmation(contact, { redirect: true })}
        />
      )}

      {dialog}
    </>
  );
}

function Filters({ values, isOpen, onClose, onApply }) {
  const [filters, setFilters] = useState(values);

  const integrations = useIntegrations();

  const hubspotEnabled = integrations?.hubspot;
  const salesforceEnabled = integrations?.salesforce;

  const handleApply = () => {
    onApply(filters);
    onClose();
  };

  const handleFilter = (filter) => {
    setFilters({ ...filters, ...filter });
  };

  const handleCancel = () => {
    setFilters(values);
    onClose();
  };

  useEffect(() => {
    setFilters(values);
  }, [values]);

  return (
    <FiltersDrawer isOpen={isOpen} onApply={handleApply} onClose={handleCancel}>
      {hubspotEnabled && (
        <YesNoFilter
          icon="filter"
          clearable
          placeholder="HubSpot Synced"
          value={filters.contactHubspotSynced}
          onChange={({ target: { value } }) => handleFilter({ contactHubspotSynced: value })}
        />
      )}

      {salesforceEnabled && (
        <YesNoFilter
          icon="filter"
          clearable
          placeholder="Salesforce Synced"
          value={filters.contactSalesforceSynced}
          onChange={({ target: { value } }) => handleFilter({ contactSalesforceSynced: value })}
        />
      )}

      <FiltersGroup label="Client Filters" filters={[filters.clients]}>
        <ClientFilter
          name="client"
          placeholder="Client"
          materialAlwaysVisible
          value={filters.clients}
          onChange={({ target: { value } }) => handleFilter({ clients: value })}
        />
      </FiltersGroup>

      <FiltersGroup label="Company Filters" filters={[filters.companies]}>
        <CompanyFilter
          name="company"
          placeholder="Company"
          materialAlwaysVisible
          value={filters.companies}
          onChange={({ target: { value } }) => handleFilter({ companies: value })}
        />
      </FiltersGroup>

      <FiltersGroup label="Opportunity Filters" filters={[filters.opportunities]}>
        <OpportunityFilter
          name="opportunity"
          placeholder="Opportunity"
          materialAlwaysVisible
          value={filters.opportunities}
          onChange={({ target: { value } }) => handleFilter({ opportunities: value })}
        />
      </FiltersGroup>

      <ContactFiltersGroup filters={filters} onChange={handleFilter} />
    </FiltersDrawer>
  );
}
